import React, { useState } from 'react';
import './ProviderDashboard.css';
import KitResults from './KitResults';
import { EyeIcon } from '@heroicons/react/24/outline';
import { usePaginatedKits } from './hooks/usePaginatedKits';
import { useProviderBootstrap } from '../common/hooks/useProviderBootstrap';
import { Pagination, FilterSidebar } from './components/DashboardCommon';
import KitsTable from './components/KitsTable';
import TableSection from './components/TableSection';
import DashboardLayout from './components/DashboardLayout';
import { createKitFetcher } from './utils/kitApiFetcher';
import { STATUS_MAPPING, EmptyTableMessage, LoadingSpinner, createSearchKeyHandler } from './utils/dashboardConstants';
import { createPatientNameColumn, createStatusColumn, createResultsColumn, createCollectionDateColumn, createResultsDateColumn, type TableColumn, type BaseKit } from './utils/tableColumns';

// Define types for our data
interface Kit extends BaseKit {
  id?: string;
  customer_email: string;
  kitType?: string;
  claim_timestamp: number | null;
  notes: string | null;
  customer_id: string | null;
}

const FILTER_OPTIONS = [
  { label: "All Results", value: null },
  { label: "Draft", value: STATUS_MAPPING.DRAFT },
  { label: "Compiled", value: STATUS_MAPPING.COMPILED },
  { label: "On Sale", value: STATUS_MAPPING.ON_SALE },
  { label: "Claimed", value: STATUS_MAPPING.CLAIMED },
  { label: "Sample Collection In Progress", value: STATUS_MAPPING.SAMPLE_COLLECTION_IN_PROGRESS },
  { label: "Preparing for Shipping to Lab", value: STATUS_MAPPING.PREPARING_FOR_SHIPPING_TO_LAB },
  { label: "Results Ready", value: STATUS_MAPPING.SUCCESSFULLY_PROCESSED_BY_LAB },
  { label: "Completed", value: STATUS_MAPPING.COMPLETED }
];

const ProviderDashboard: React.FC = () => {
  const {
    kits,
    totalResults,
    loading,
    error,
    setError,
    currentPage,
    totalPages,
    selectedFilter,
    searchQuery,
    setSearchQuery,
    handleSearch,
    handlePageChange,
    handleFilterChange: applyFilter,
    handleSort,
    sortBy,
    sortOrder,
    fetchPage,
  } = usePaginatedKits<Kit>(createKitFetcher('kits'));

  const [selectedResult, setSelectedResult] = useState<{
    resultId: string;
    serialNumber: string;
  } | null>(null);

  const { clinic } = useProviderBootstrap({
    onReady: async () => {
      await fetchPage(1);
    },
    setError,
    onIdentity: () => undefined,
  });

  const handleKeyDown = createSearchKeyHandler(handleSearch);

  const tableColumns: TableColumn<Kit>[] = [
    {
      header: "Kit ID",
      className: "kit-id",
      render: (kit: Kit) => (
        <span className="kit-number">{kit.serial_number}</span>
      ),
    },
    createPatientNameColumn<Kit>(),
    createStatusColumn<Kit>(),
    createResultsColumn<Kit>(),
    createCollectionDateColumn<Kit>(),
    createResultsDateColumn<Kit>(),
    {
      header: "Actions",
      className: "action-cell sticky-column",
      render: (kit: Kit) =>
        kit.has_results && kit.results_id ? (
          <button
            className="view-results-button"
            onClick={() =>
              setSelectedResult({
                resultId: kit.results_id!,
                serialNumber: kit.serial_number,
              })
            }
          >
            <EyeIcon className="button-icon" />
            View Results
          </button>
        ) : null,
    },
  ];

  if (loading && kits.length === 0) {
    return <LoadingSpinner />;
  }

  const tableSection = (
    <div className="table-container">
      <TableSection
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        searchPlaceholder="Search by Kit ID, Patient Name"
        resultsCount={totalResults}
      >
        <KitsTable
          data={kits}
          columns={tableColumns}
          loading={loading}
          rowKey={(kit) => kit.serial_number}
          emptyMessage={<EmptyTableMessage searchQuery={searchQuery} selectedFilter={selectedFilter} />}
          loadingMessage="Loading results..."
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </TableSection>
    </div>
  );

  return (
    <DashboardLayout
      title="Patient Test Results"
      error={error}
      onDismissError={() => setError(null)}
      tableSection={tableSection}
      pagination={<Pagination totalPages={totalPages} currentPage={currentPage} onChange={handlePageChange} />}
      filterSidebar={
        <FilterSidebar
          options={FILTER_OPTIONS}
          selectedFilter={selectedFilter}
          onChange={applyFilter}
          clinic={clinic}
        />
      }
    >
      {selectedResult && (
        <KitResults
          serialNumber={selectedResult.serialNumber}
          onClose={() => setSelectedResult(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default ProviderDashboard;